/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, NgZone, OnDestroy, OnInit} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {NgForm} from '@angular/forms';
import {AngularModalService} from '../../common/modal/angular-modal.service';
import {Subscription} from 'rxjs';
import * as _ from 'lodash';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import template from './user-profile-page.component.html';

import {ChangePasswordModalComponent} from './change-password-modal.component';
import {CreatePasskeyModalComponent} from './create-passkey-modal.component';

import {LOCALES} from '../../common/constants/locales';
import {SESSION_EVENT} from '../../common/constants/misc-constants';
import {LocaleHelper} from '../../../components/util/locale-helper';
import {gravatarHash} from '../../../components/util/gravatar-hash';
import {LocalPausing} from '../../../components/session/local-pausing';
import {
  clearFormValidation,
  createFormSubmitState,
  exposeFormSubmitStateAccessors,
  FormControlError,
  FormSubmitState,
  markFormSubmitSuccess,
  hasFormValidationError,
} from '../../common/controller/form-submit-state';
import {AdnTranslateService} from '../../common/filters/adn-translate.service';
import {UserResource} from '../../../components/api/resources/resource/user.resource';
import {UserProfileResource} from '../../../components/api/resources/resource/user-profile.resource';
import {AuthenticatedUserResource} from '../../../components/api/resources/resource/authenticated-user';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {WebAuthnResource} from '../../../components/api/resources/resource/webauthn';
import {WINDOW} from '../../angular-platform-providers';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-user-profile-page',
  template
})
export class UserProfilePageComponent implements OnInit, OnDestroy {
  readonly locales = LOCALES;

  user: any;
  model: any = {};
  profile: any = {};
  passkeys: any[] | null = null;
  originalPasskeys: any[] = [];
  deletedPasskeys: any[] = [];
  showDisabled = false;
  showLoginWithPasskey = false;
  isMasquerading = false;
  allowPausing = false;
  isSpecialUser = false;
  enablePausingAdUnits = false;
  currencies: string[] = [];
  defaultCurrency: string;
  registerPasskeyStarted = false;
  descriptionEditing: any = null;
  wrongCurrencyForNetwork = false;
  startedProcess = false;
  loading: string | boolean = false;
  cancelLoading: string | boolean = false;
  verifyErr: any = null;
  cancelErr: any = null;
  startErr: any = null;
  verifyCode = '';
  qrCode: string | null = null;

  submitState: FormSubmitState = createFormSubmitState();
  pending = false;

  private readonly profileForSaving: Record<string, any> = {};
  private _registeredForm: NgForm | null = null;
  private _formSub: Subscription | null = null;
  private _formSettleTimer: any = null;
  private _initializingForm = false;
  private _angularFormDirty = false;
  private readonly formControlErrors: FormControlError[] = [];

  constructor(
    private readonly eventBus: AppEventBusService,
    @Inject(WINDOW) window: any,
    private readonly $state: StateService,
    @Inject(UserResource) private readonly User: any,
    private readonly toastr: ToastrService,
    @Inject(UserProfileResource) private readonly UserProfile: any,
    @Inject(WebAuthnResource) private readonly webAuthnResource: any,
    @Inject(AuthenticatedUserResource) private readonly authenticatedUserResource: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly ngZone: NgZone,
    private readonly angularModalService: AngularModalService,
    private readonly adnTranslate: AdnTranslateService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.showLoginWithPasskey = !!window.PublicKeyCredential;
    this.currencies = LocalNetworkProfile.get('availableCurrencies') || [];
    this.defaultCurrency = LocalNetworkProfile.get('defaultCurrency');
    exposeFormSubmitStateAccessors(this, this.submitState, {
      getRegisteredForm: () => this._registeredForm
    });
  }

  ngOnInit(): void {
    this.user = this.localUserProfile.get();
    if (!this.user) {
      return;
    }

    this.isMasquerading = this.localUserProfile.isMasquerading();
    this.allowPausing = this.localUserProfile.canPauseAdUnits();
    this.isSpecialUser = this.localUserProfile.get('username') === 'adnuntius@adnuntius.com'
      || this.localUserProfile.get('username') === 'broker1@adnuntius.com';
    this.enablePausingAdUnits = LocalPausing.obtain().get();

    this.init();
    this.loadPasskeys();
  }

  ngOnDestroy(): void {
    this._formSub?.unsubscribe();
    clearTimeout(this._formSettleTimer);
  }

  get gravatarUrl(): string {
    const hash = gravatarHash(this.user?.username);
    return `https://secure.gravatar.com/avatar/${hash}?s=80&d=identicon`;
  }

  translateError(err: {text?: string; code?: string; parameters?: any}): string {
    if (!err) {
      return '';
    }
    return this.adnTranslate.transform(err.text, err.code, err.parameters);
  }

  registerForm(editForm: NgForm): void {
    if (this._registeredForm === editForm) {
      return;
    }
    this._registeredForm = editForm;
    this._startTrackingForm();
  }

  submit(editForm: NgForm): void {
    if (this.pending || this.isSaveDisabled(editForm)) {
      return;
    }

    this.pending = true;
    this.submitState.ngForm = editForm;
    clearFormValidation(this.submitState, this.formControlErrors);

    LocalPausing.obtain().set(this.enablePausingAdUnits || false);

    const saveModel = {
      id: this.model.id,
      locale: this.model.locale,
      name: this.model.name,
      surname: this.model.surname,
      displayName: this.model.displayName,
      preferredCurrency: this.model.preferredCurrency,
      concise: true
    };

    this.User.save(saveModel, (savedUser: any) => {
      this.ngZone.run(() => {
        this.localUserProfile.set('name', savedUser.name);
        this.localUserProfile.set('surname', savedUser.surname);
        this.localUserProfile.set('displayName', savedUser.displayName);
        this.localUserProfile.set('locale', savedUser.locale);
        this.localUserProfile.set('twoFactorAuthType', savedUser.twoFactorAuthType);
        this.localUserProfile.set('login2faRequired', savedUser.login2faRequired);
        this.localUserProfile.set('canMasquerade', savedUser.canMasquerade);
        this.localUserProfile.set('preferredCurrency', savedUser.preferredCurrency);
        this.localUserProfile.set('passwordExpiry', savedUser.passwordExpiry);
        this.localUserProfile.set('passwordExpiresDays', savedUser.passwordExpiresDays);

        this.qrCode = null;
        if (savedUser.twoFactorAuthConfig) {
          this.qrCode = savedUser.twoFactorAuthConfig.qrCode;
        }

        this.profileForSaving.adnPrefs = this.profile.adnPrefs;
        this.UserProfile.save({
          id: this.user.userId,
          forceApiSave: true,
          profile: this.profileForSaving
        }, (apiUser: any) => {
          this.ngZone.run(() => {
            this.user = this.localUserProfile.safeSaveProfile('adnPrefs', apiUser.profile.adnPrefs);
            this.profile = _.cloneDeep(this.user.profile);
            this.init();

            this.localUserProfile.publish('profile', this.user.profile);

            markFormSubmitSuccess(this.submitState, editForm);
            this._angularFormDirty = false;
            this._startTrackingForm();

            this.eventBus.emit(SESSION_EVENT.userProfileUpdate, this.user);
            this.pending = false;
            this.eventBus.emit('SUBMIT_PROCESSED');
          });
        });
      });
    });
  }

  cancel(editForm: NgForm): void {
    if (this.isSaveDisabled(editForm)) {
      return;
    }
    this.init();
    this.submitState.validSubmission = false;
    this._angularFormDirty = false;
    editForm.control.markAsPristine();
  }

  isFormPristine(editForm: NgForm): boolean {
    void editForm;
    return !this._angularFormDirty;
  }

  isSaveDisabled(editForm: NgForm): boolean {
    return this.isFormPristine(editForm)
      || this.pending
      || (editForm.invalid && !hasFormValidationError(this.submitState));
  }

  goDefault(): void {
    this.model.preferredCurrency = null;
    this.wrongCurrencyForNetwork = false;
    this._registeredForm?.control.markAsDirty();
    this._angularFormDirty = true;
  }

  passkeyToggle(): void {
    this.passkeys = _.filter(this.originalPasskeys, (p) => this.showDisabled || p.enabled);
  }

  startEditingDescription(pk: any): void {
    if (this.descriptionEditing && this.descriptionEditing !== pk) {
      this.descriptionEditing.editing = false;
    }
    pk.editing = true;
    this.descriptionEditing = pk;
  }

  stopEditingDescription(pk: any): void {
    pk.editing = false;
  }

  saveEditingDescription(pk: any): void {
    pk.editing = false;
    if (this.descriptionEditing === pk) {
      this.descriptionEditing = null;
      this.webAuthnResource.updateDescription(pk.id, pk.description).then(() => {
        this.reloadPage();
      });
    }
  }

  deleteCredential(id: string): void {
    this.webAuthnResource.deleteCredential(id).then(() => {
      this.reloadPage();
    });
  }

  restoreCredential(id: string): void {
    this.webAuthnResource.restoreCredential(id).then(() => {
      this.reloadPage();
    });
  }

  registerPasskey(): void {
    this.registerPasskeyStarted = true;
    this.webAuthnResource.registerCredentialsOptions().then((payload: any) => {
      if (payload) {
        this.openRegisterPasskeyDialog(payload);
      }
      // Modal attach schedules a tick; defer unlock so parent [disabled] does not NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.registerPasskeyStarted = false;
      });
    }).catch((err: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.registerPasskeyStarted = false;
        this.toastr.warning(err.text, 'Passkey Registration Failed!');
      });
    });
  }

  changePasswordDialog(): void {
    const modalRef = this.angularModalService.show(ChangePasswordModalComponent, {
      backdrop: 'static',
      initialState: {user: this.user}
    });
    (modalRef.content as ChangePasswordModalComponent).onCompleted.subscribe(() => {
      this.reloadPage();
    });
  }

  startProcess(twoFactorAuthType: string): void {
    this.startedProcess = true;
    this.loading = 'LOADING';
    this.verifyErr = false;
    this.cancelErr = false;
    this.startErr = false;
    this.verifyCode = '';

    const saveModel = {
      id: this.model.id,
      concise: true,
      twoFactorAuthType,
      login2faRequired: false
    };

    this.User.save(saveModel, (savedUser: any) => {
      this.ngZone.run(() => {
        this.localUserProfile.set('twoFactorAuthType', savedUser.twoFactorAuthType);
        this.localUserProfile.set('login2faRequired', savedUser.login2faRequired);

        if (savedUser.twoFactorAuthType === 'APP') {
          this.qrCode = _.get(savedUser, ['twoFactorAuthConfig', 'qrCode']);
        } else {
          this.qrCode = null;
        }
        this.model.twoFactorAuthType = savedUser.twoFactorAuthType;
        this.loading = 'LOADED';
      });
    }, (err: any) => {
      this.ngZone.run(() => {
        this.loading = 'FAILED';
        this.startErr = _.get(err, ['data']) || {
          text: 'An error occurred in the 2FA cancellation process',
          code: '',
          parameters: {}
        };
      });
    });
  }

  cancel2fa(): void {
    this.cancelLoading = 'LOADING';
    this.verifyErr = false;
    this.cancelErr = false;
    this.startErr = false;

    const saveModel = {
      id: this.model.id,
      concise: true,
      twoFactorAuthType: 'NONE',
      login2faRequired: false
    };

    this.User.save(saveModel, (savedUser: any) => {
      this.ngZone.run(() => {
        this.localUserProfile.set('twoFactorAuthType', savedUser.twoFactorAuthType);
        this.localUserProfile.set('login2faRequired', !!savedUser.login2faRequired);
        this.loading = false;
        this.qrCode = null;
        this.startedProcess = false;
        this.cancelLoading = 'LOADED';
        this.init();
      });
    }, (err: any) => {
      this.ngZone.run(() => {
        this.cancelLoading = 'FAILED';
        this.cancelErr = _.get(err, ['data']) || {
          text: 'An error occurred in the 2FA cancellation process',
          code: '',
          parameters: {}
        };
      });
    });
  }

  verify(): void {
    this.cancelLoading = 'LOADING';
    this.verifyErr = false;
    this.cancelErr = false;
    this.startErr = false;

    this.authenticatedUserResource.verify2fa(this.verifyCode).then((result: any) => {
      if (result === true) {
        const saveModel = {
          id: this.model.id,
          concise: true,
          login2faRequired: true
        };
        this.User.save(saveModel, (savedUser: any) => {
          this.ngZone.run(() => {
            this.localUserProfile.set('login2faRequired', !!savedUser.login2faRequired);
            this.localUserProfile.set('passwordExpiry', savedUser.passwordExpiry);
            this.localUserProfile.set('passwordExpiresDays', savedUser.passwordExpiresDays);
            this.loading = false;
            this.qrCode = null;
            this.startedProcess = false;
            this.init();
            this.cancelLoading = 'LOADED';
            this.reloadPage();
          });
        }, () => {
          this.ngZone.run(() => {
            this.cancelLoading = 'FAILED';
            this.verifyErr = {
              text: 'The verification process failed. Try again or reload the page to proceed.',
              code: '',
              parameters: {}
            };
          });
        });
      } else {
        this.ngZone.run(() => {
          this.verifyErr = {
            text: 'Your 2FA code was not verified successfully. Resubmit your code or cancel and start again.',
            code: '',
            parameters: {}
          };
          this.cancelLoading = 'FAILED';
        });
      }
    }).catch((res: any) => {
      this.ngZone.run(() => {
        this.verifyErr = _.get(res, ['data']) || {
          text: 'An error occurred in the verification process.',
          code: '',
          parameters: {}
        };
        this.cancelLoading = 'FAILED';
      });
    });
  }

  private openRegisterPasskeyDialog(registerCredentials: any): void {
    const modalRef = this.angularModalService.show(CreatePasskeyModalComponent, {
      initialState: {
        user: this.user,
        registerCredentials,
        passkeyCount: this.passkeys?.length || 0
      }
    });
    (modalRef.content as CreatePasskeyModalComponent).onCompleted.subscribe(() => {
      this.reloadPage();
    });
  }

  private reloadPage(): void {
    this.$state.go('app.adm.user-profile', {}, {reload: true, inherit: false});
  }

  private loadPasskeys(): void {
    this.webAuthnResource.existingCredentials().$promise.then((page: any) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.originalPasskeys = page || [];
        this.deletedPasskeys = _.filter(this.originalPasskeys, (p) => !p.enabled);
        this.passkeys = _.filter(this.originalPasskeys, (p) => this.showDisabled || p.enabled);
      });
    });
  }

  private init(): void {
    this.profile = _.cloneDeep(this.user.profile);
    this.profile.adnPrefs.locale = LocaleHelper.toApiFormat(this.profile.adnPrefs.locale);
    this.profile.adnPrefs.defaultPageSize = this.profile.adnPrefs.defaultPageSize || 25;
    this.profile.adnPrefs.showReleaseHighlight = this.profile.adnPrefs.showReleaseHighlight || 'SHOW';

    delete this.profile.adnPrefs.excelDownloadFormat;
    delete this.profile.adnPrefs.dropdownMenu;
    delete this.profile.adnPrefs.layoutVersion;
    delete this.profile.adnPrefs.menuVersion;
    delete this.profile.adnPrefs.menuClosed;
    delete this.profile.adnPrefs.preferredCurrency;
    delete this.profile.adnPrefs.showSupportWidget;

    this.model = {
      username: this.localUserProfile.get('username'),
      id: this.localUserProfile.get('userId'),
      locale: this.localUserProfile.get('locale'),
      name: this.localUserProfile.get('name'),
      surname: this.localUserProfile.get('surname'),
      displayName: this.localUserProfile.get('displayName'),
      twoFactorAuthType: this.localUserProfile.get('twoFactorAuthType'),
      login2faRequired: this.localUserProfile.get('login2faRequired'),
      canMasquerade: this.localUserProfile.get('canMasquerade'),
      preferredCurrency: this.localUserProfile.getPreferredCurrency(),
      passwordExpiry: this.localUserProfile.get('passwordExpiry'),
      passwordExpiresDays: this.localUserProfile.get('passwordExpiresDays')
    };

    this.wrongCurrencyForNetwork = !!(this.model.preferredCurrency
      && _.indexOf(this.currencies, this.model.preferredCurrency) < 0);

    if (this.user.username === 'adnuntius@adnuntius.com') {
      _.forEach(Object.keys(this.profile), (k) => {
        if (_.startsWith(k, 'adnLastViewed') || _.startsWith(k, 'adnLastEdited') || _.startsWith(k, 'undefined')) {
          this.profileForSaving[k] = null;
        }
      });
    }
  }

  private _startTrackingForm(): void {
    this._formSub?.unsubscribe();
    clearTimeout(this._formSettleTimer);
    this._initializingForm = true;
    this._angularFormDirty = false;
    this._formSettleTimer = setTimeout(() => {
      if (!this._registeredForm) {
        return;
      }
      this._registeredForm.control.markAsPristine();
      this._registeredForm.control.markAsUntouched();
      this._angularFormDirty = false;
      this._initializingForm = false;
      this._formSub = this._registeredForm.valueChanges?.subscribe(() => {
        if (!this._initializingForm && !this._angularFormDirty) {
          this._angularFormDirty = true;
        }
      }) || null;
    }, 500);
  }
}
